import React from "react";
import {
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { ComparisonClassification, precisionOf, recallOf } from "./detectionMatch";

export type ComparisonFilter = "all" | "matched" | "aiOnly" | "missed";

interface ComparisonMetricsPanelProps {
  classification: ComparisonClassification<any, any>;
  groundTruthCount: number;
  // When provided, the counts on each table's totals row become clickable and
  // filter the tables + pipe to that category (click the active one to clear).
  // Per-code rows are read-only: the filter has no per-code dimension.
  activeFilter?: ComparisonFilter;
  onSelectFilter?: (filter: ComparisonFilter) => void;
  /**
   * Render one card only, so each can sit above the table it describes: "inspector" above
   * Inspector (Ground Truth), "ai" above AI Analytics. Omitted, both render side by side.
   */
  side?: "ai" | "inspector";
}

const pct = (v: number | null) => (v == null ? "—" : `${Math.round(v * 100)}%`);
const ratio = (num: number, den: number) => (den > 0 ? num / den : null);

/**
 * Agreement summary for the Comparison tab, as two mirrored tables — each a
 * totals row that expands into one row per main code.
 *
 *  - "AI vs Inspector" reads the model's output: how many of its detections the
 *    inspector confirms (matched), how many it added (AI only), and precision.
 *  - "Inspector vs AI" reads the inspector's list: how many of those rows the AI
 *    backs up (confirmed), how many it never found, and coverage (recall).
 *
 * Purely derived from the classification, so both react to the confidence and
 * tolerance controls automatically.
 */
const ComparisonMetricsPanel: React.FC<ComparisonMetricsPanelProps> = ({
  classification,
  groundTruthCount,
  activeFilter = "all",
  onSelectFilter,
  side,
}) => {
  // Per-code detail is collapsed by default — the totals are what the reviewer
  // scans; each table remembers its own state.
  const [aiOpen, setAiOpen] = React.useState(false);
  const [inspectorOpen, setInspectorOpen] = React.useState(false);

  const tp = classification.matched.length;
  const fp = classification.aiOnly.length;
  const fn = classification.inspectorOnly.length;
  const precision = precisionOf(classification);
  const recall = recallOf(classification, groundTruthCount);

  const codes = Array.from(classification.byCode.entries()).sort((a, b) =>
    a[0].localeCompare(b[0])
  );

  // A count cell. `filter` is only passed on a totals row, the only row the
  // metric filter can act on.
  const countCell = (
    value: number,
    color: string,
    title: string,
    filter?: ComparisonFilter
  ) => {
    const clickable = !!filter && !!onSelectFilter;
    const active = clickable && activeFilter === filter;
    return (
      <TableCell align="center" sx={{ py: 0.5 }}>
        <Tooltip title={clickable ? `${title} — click to filter` : title}>
          <Box
            component="span"
            onClick={clickable ? () => onSelectFilter!(active ? "all" : filter!) : undefined}
            sx={{
              display: "inline-block",
              minWidth: 34,
              px: 0.75,
              borderRadius: 1,
              border: "2px solid",
              borderColor: active ? color : "transparent",
              backgroundColor: active ? "action.selected" : "transparent",
              color,
              fontWeight: 700,
              cursor: clickable ? "pointer" : "default",
              userSelect: "none",
            }}
          >
            {value}
          </Box>
        </Tooltip>
      </TableCell>
    );
  };

  const headCell = (label: string, title: string, align: "left" | "center" = "center") => (
    <TableCell key={label} align={align} sx={{ py: 0.5, fontWeight: 600, lineHeight: 1.3 }}>
      <Tooltip title={title}>
        <span>{label}</span>
      </Tooltip>
    </TableCell>
  );

  // The "All codes" cell doubles as the expander for that table's per-code rows.
  // The toggle sits on this cell only, so it never swallows a click on the
  // filterable count cells in the same row.
  const totalsLabelCell = (open: boolean, toggle: () => void, detailCount: number) => (
    <TableCell sx={{ py: 0.5, fontWeight: 600, whiteSpace: "nowrap" }}>
      {detailCount > 0 ? (
        <Tooltip
          title={
            open
              ? "Hide the per-code breakdown"
              : `Show the per-code breakdown (${detailCount} codes)`
          }
        >
          <Box
            component="span"
            onClick={toggle}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.25,
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            All codes
          </Box>
        </Tooltip>
      ) : (
        <Box component="span" sx={{ pl: 2.5 }}>
          All codes
        </Box>
      )}
    </TableCell>
  );

  // Both cards stay on one row and share the width; a table wider than its share
  // scrolls inside its own card rather than pushing the pair onto two lines.
  const card = (title: string, body: React.ReactNode) => (
    <Box
      sx={{
        flex: "1 1 0",
        minWidth: 0,
        px: 2,
        py: 1,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Divider sx={{ mt: 1, mb: 0.5 }} />
      {body}
    </Box>
  );

  // Fixed layout: both tables have five columns, so they end up with identical
  // column geometry and always fit their half — long headers wrap instead of
  // forcing a horizontal scrollbar.
  const tableSx = {
    width: "100%",
    tableLayout: "fixed" as const,
    fontVariantNumeric: "tabular-nums" as const,
    "& td, & th": { borderColor: "divider", px: 1 },
  };

  // ---- AI vs Inspector: the model's detections. A code the AI never proposed
  // would be an all-zero row here, so it only shows up in the inspector table.
  const aiCodes = codes.filter(([, c]) => c.tp + c.fp > 0);
  const aiTable = (
    <Table size="small" sx={tableSx}>
      <TableHead>
        <TableRow>
          {headCell("Code", "Damage main code", "left")}
          {headCell("Detections", "All detections the AI proposed")}
          {headCell("Matched", "AI detections that match an inspector detection (within tolerance)")}
          {headCell("AI only", "AI detections the inspector did not record")}
          {headCell("Precision", "Matched ÷ all AI detections")}
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow sx={{ backgroundColor: "action.hover" }}>
          {totalsLabelCell(aiOpen, () => setAiOpen(!aiOpen), aiCodes.length)}
          <TableCell align="center" sx={{ py: 0.5, fontWeight: 600 }}>{tp + fp}</TableCell>
          {countCell(tp, "success.main", "AI detections that match an inspector detection (within tolerance)", "matched")}
          {countCell(fp, "warning.main", "AI detections the inspector did not record", "aiOnly")}
          <TableCell align="center" sx={{ py: 0.5, fontWeight: 600 }}>{pct(precision)}</TableCell>
        </TableRow>

        {aiOpen &&
          aiCodes.map(([code, c]) => (
            <TableRow key={code}>
              <TableCell sx={{ py: 0.5, pl: 4.5, wordBreak: "break-word" }}>{code}</TableCell>
              <TableCell align="center" sx={{ py: 0.5 }}>{c.tp + c.fp}</TableCell>
              {countCell(c.tp, "success.main", `${code}: AI detections matching the inspector`)}
              {countCell(c.fp, "warning.main", `${code}: AI detections the inspector did not record`)}
              <TableCell align="center" sx={{ py: 0.5 }}>{pct(ratio(c.tp, c.tp + c.fp))}</TableCell>
            </TableRow>
          ))}
      </TableBody>
    </Table>
  );

  // ---- Inspector vs AI: the inspector's own rows. `gt` counts the rows they
  // recorded for a code (not tp + fn — several AI detections can match one
  // inspector row), so confirmed = gt - fn.
  const gtCodes = codes.filter(([, c]) => c.gt > 0);
  const inspectorTable =
    groundTruthCount > 0 ? (
      <Table size="small" sx={tableSx}>
        <TableHead>
          <TableRow>
            {headCell("Code", "Damage main code", "left")}
            {headCell("Damages", "Damages the inspector recorded")}
            {headCell("Confirmed", "Inspector detections the AI also found (within tolerance)")}
            {headCell("Not found", "Inspector detections the AI missed")}
            {headCell("Coverage", "Confirmed ÷ all inspector detections (recall)")}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow sx={{ backgroundColor: "action.hover" }}>
            {totalsLabelCell(inspectorOpen, () => setInspectorOpen(!inspectorOpen), gtCodes.length)}
            <TableCell align="center" sx={{ py: 0.5, fontWeight: 600 }}>{groundTruthCount}</TableCell>
            {countCell(groundTruthCount - fn, "success.main", "Inspector detections the AI also found (within tolerance)", "matched")}
            {countCell(fn, "error.main", "Inspector detections the AI missed", "missed")}
            <TableCell align="center" sx={{ py: 0.5, fontWeight: 600 }}>{pct(recall)}</TableCell>
          </TableRow>

          {inspectorOpen &&
            gtCodes.map(([code, c]) => (
              <TableRow key={code}>
                <TableCell sx={{ py: 0.5, pl: 4.5, wordBreak: "break-word" }}>{code}</TableCell>
                <TableCell align="center" sx={{ py: 0.5 }}>{c.gt}</TableCell>
                {countCell(c.gt - c.fn, "success.main", `${code}: inspector detections the AI also found`)}
                {countCell(c.fn, "error.main", `${code}: inspector detections the AI missed`)}
                <TableCell align="center" sx={{ py: 0.5 }}>{pct(ratio(c.gt - c.fn, c.gt))}</TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    ) : (
      <Typography variant="caption" color="text.secondary">
        No inspector detections recorded yet.
      </Typography>
    );

  return (
    <Box
      data-html2canvas-ignore
      sx={{ display: "flex", flexWrap: "nowrap", alignItems: "flex-start", gap: 2, mb: 2 }}
    >
      {side !== "inspector" && card("AI vs Inspector", aiTable)}
      {side !== "ai" && card("Inspector vs AI", inspectorTable)}
    </Box>
  );
};

export default ComparisonMetricsPanel;
